// Components
import EmailSVG from "../../../assets/media/svg/icons/email.svg";

// WP / React stuff
import { useReactiveVar } from "@apollo/client";
import { __ } from "@wordpress/i18n";
import { useEffect, useState } from "react";
import { form, result } from "../../stores/index";
import { FormState } from "@stores/form.ts";
import { RootElementDataset } from "@types/RootElementDataset.ts";
import ToolDetailBanner from "./ToolDetailBanner";

/**
 * Root element for rendering the Tool component.
 */
const rootElement = document.querySelector(
  '[data-block="tool-block"]',
) as HTMLElement;

// Helpers
const flattenObject = (
  obj: Record<string, any>,
  parentKey = "",
  separator = "_",
): Record<string, any> => {
  return Object.entries(obj).reduce(
    (acc, [key, value]) => {
      const newKey = parentKey ? `${parentKey}${separator}${key}` : key;

      if (value && typeof value === "object" && !Array.isArray(value)) {
        Object.assign(acc, flattenObject(value, newKey, separator));
      } else {
        acc[newKey] = value;
      }

      return acc;
    },
    {} as Record<string, any>,
  );
};

const remapSubmittedByClinicName = (formData: FormState) => {
  const map = new Map<string, string>();
  for (const f of formData.data?.fields ?? []) {
    if (f?.name && f.clinicName) map.set(f.name, f.clinicName);
  }
  const out: Record<string, any> = {};
  for (const [k, v] of Object.entries(formData.submitted ?? {})) {
    out[map.get(k) ?? k] = v;
  }
  return out;
};

const removeLargeArrays = (obj: any, maxLength = 50): any => {
  if (Array.isArray(obj)) {
    // drop large arrays entirely
    return obj.length > maxLength
      ? undefined
      : obj.map((v) => removeLargeArrays(v, maxLength));
  }
  if (obj && typeof obj === "object") {
    return Object.fromEntries(
      Object.entries(obj)
        .map(([k, v]) => [k, removeLargeArrays(v, maxLength)])
        .filter(([, v]) => v !== undefined),
    );
  }
  return obj;
};
const Cta = () => {
  // TODO: remove enableShareSimulation and enableCtaShortener logics - they seem to be not needed here...
  const [sectionData, setSectionData] = useState<RootElementDataset>({
    clinicSection: false,
    clinicSectionTitle: "",
    clinicSectionDescription: "",
    clinicSectionButtonLabel: "",
    clinicSectionButtonDestination: "",
    clinicSectionButtonLink: "",
    clinicSectionButtonForm: "",
    clinicSectionButtonFormPage: "",
    clinicSectionPType: "",
    clinicSectionOperation: "",
    clinicSectionSource: "",
    emailSend: false,
    emailSendButtonLabel: "",
    emailSendForm: "",
    enableShareSimulation: false,
    enableCtaShortener: false,
  });

  const formData: FormState = useReactiveVar(form);
  const { response: formResponse } = useReactiveVar(result);

  const [emailParams, setEmailParams] = useState<Record<string, any>>({});
  const [fullLink, setFullLink] = useState("#");

  // Init section data from root element
  useEffect(() => {
    if (!rootElement) return;
    try {
      const { attributes } = rootElement.dataset;
      if (attributes) {
        const parsed = JSON.parse(attributes);
        setSectionData(parsed);
      }
    } catch (e) {
      console.error("Failed to parse section data:", e);
    }
  }, []);

  // Prepare CTA state + params (used for email AND for GF prefill)
  useEffect(() => {
    const remappedSubmitted = remapSubmittedByClinicName(formData);

    //const cleaned = removeLargeArrays(formResponse?.data?.result || {}, 6); // remove semester or higher results
    const cleaned = removeLargeArrays(
      formResponse?.data?.result?.recommended
        ? formResponse.data.result.recommended
        : formResponse?.data?.result || {},
      6,
    );
    const resultFlat = flattenObject(cleaned);

    const candidate = String(sectionData?.clinicSectionSource ?? "").trim();
    const typePart = String(formData?.type ?? "").trim() || "tool";

    const formSrc = candidate || `ch.sim.inform.${typePart}`;

    const combined = {
      ...remappedSubmitted,
      ...resultFlat,
      ...(sectionData.clinicSectionPType !== undefined && {
        processType: sectionData.clinicSectionPType,
      }),
      ...(sectionData.clinicSectionOperation !== undefined && {
        operationType: sectionData.clinicSectionOperation,
      }),
      src: formSrc,
    };

    setEmailParams(combined);

    if (sectionData.clinicSection) {
      const query = new URLSearchParams(combined).toString();
      setFullLink(`${sectionData.clinicSectionButtonLink}?${query}`);
    }
  }, [sectionData, formData, formResponse]);

  return (
    <>
      {sectionData.emailSend && (
        <div className="cta-email">
          <button
            type="button"
            data-module="modal"
            data-form-id={sectionData.emailSendForm}
            data-modal-type="email-tool-result"
            data-form-params={JSON.stringify(emailParams)}
            className="btn btn--simple"
          >
            <EmailSVG />
            {sectionData.emailSendButtonLabel}
          </button>
        </div>
      )}

      {/* Use the ToolDetailBanner Component */}
      {sectionData.clinicSection && (
        <ToolDetailBanner
          {...sectionData}
          emailParams={emailParams}
          fullLink={fullLink}
        />
      )}
    </>
  );
};

export default Cta;
